import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Confirm, Icon, Modal, Message, Pagination, Table, Grid, Segment, Loader } from 'semantic-ui-react';
import helpers, { cancelToken, STATUS_INIT, STATUS_LOADING, STATUS_NO_RESULTS, STATUS_ERROR, STATUS_OK } from '../../services/helpers';
import ShowResultsContext from '../../contexts/ShowResultsContext';
import SearchTermContext from '../../contexts/SearchTermContext';
import PartnerContext from '../../contexts/PartnersContext';
import PageContext from '../../contexts/PageContext';
import AddPartner from './Add';
import PageSelect from '../common/PageSelect';
import ErrorMessage from '../common/ErrorMessage';
import {useAuth0} from "../../react-auth0-wrapper";


const List = (props) => {
    const [selectedPartner, setSelectedPartner] = useState({});
    const [organizations, setOrganizations] = useState([]);

    const [listPromise, setListPromise] = useState({ promise: null, source: null });
    const [id, setId] = useState("");
    const [showModalConfirm, setShowModalConfirm] = useState(false);
    const [messageModalConfirm, setMessageModalConfirm] = useState('');
    const setPartners = useContext(PartnerContext);
    const { searchTerm } = useContext(SearchTermContext);
    const { itemsPerPage } = useContext(ShowResultsContext);
    const { currentPage, setCurrentPage } = useContext(PageContext);
    const { getIdToken } = useAuth0();
    const showModal = props.showModal;
    const setShowModal = props.setShowModal;

    const { t } = useTranslation();

    useEffect(() => {
        return () => {
            if (listPromise.source !== null) {
                helpers.cancel(listPromise.promise, listPromise.source);
            }
        }
    }, [listPromise]);

    useEffect(() => {
        const source = cancelToken.source();
        helpers.auth0.getInfOrganizations(getIdToken(), source)
            .then((resp) => {
                const orgList = resp.data.orgList;
                orgList.sort((a, b) => {
                    if (a < b) return -1;
                    if (a > b) return 1;
                    return 0;
                });
                setOrganizations(orgList);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    if (props.status === STATUS_INIT) {
        return (
            <Segment>
                <Loader active size='large' inline='centered'>Loading Partners...</Loader>
            </Segment>
        );
    }

    if (props.status === STATUS_ERROR) {
        return <ErrorMessage error={props.error} />;
    }

    if (props.status === STATUS_NO_RESULTS) {
        return (
            <Message>
                <Message.Header>{t('list_message_no_results_title')}</Message.Header>
                <p>{t('list_message_no_results_text')}</p>
            </Message>);
    }

    const loadingContent = (
        <Table.Row>
            <Table.Cell colSpan='8'>
                <Segment basic>
                    <Loader active size='large' inline='centered'>Loading Partners...</Loader>
                </Segment>
            </Table.Cell>
        </Table.Row>
    );


    // pagination
    const pageNumbers = [];
    for (let x = 0; x < Math.ceil(props.totalCount / itemsPerPage); x++) {
        pageNumbers.push(x);
    }

    const resolved = (response) => {
        const { data, success } = response.data;
        const { partnerRecords, partnerCount } = data;
        if (success === true) {
            setPartners({
                list: partnerRecords,
                count: partnerCount,
                status: (partnerRecords && partnerRecords.length && partnerCount > 0) ? STATUS_OK : STATUS_NO_RESULTS
            });
        }
    };

    const handlePaginationChange = (event, { activePage }) => {
        setCurrentPage(activePage);
        setPartners({
            list: -1,
            count: props.totalCount,
            status: STATUS_LOADING
        });

        if (listPromise.source !== null) {
            helpers.cancel(listPromise.promise, listPromise.source);
        }

        const options = { uuid: `${searchTerm}`, page: activePage, limit: itemsPerPage };
        const source = cancelToken.source();
        const promise = helpers.auth0
            .search(props.listURL, options, source, getIdToken())
            .then(resolved)
            .catch(console.error);
        setListPromise({ promise, source });
    };


    // modal: Edit Partner
    const infoClick = (info) => {
        setSelectedPartner(info);
        setShowModal(true);
    };

    const onCloseModal = () => {
        setShowModal(false);
        setSelectedPartner({});
    };

    const deleteConfirm = (partner) => {
        setId(partner.id);
        setMessageModalConfirm(`Are you sure you want to delete ${partner.name}?`);
        setShowModalConfirm(true);
    };

    const deletePartner = () => {
        const token = getIdToken();
        const source = cancelToken.source();
        helpers.auth0.deletePartner(token, source, id).then(response => {
            if(response.data.success) {
                setShowModalConfirm(false);
                const msg = `Partner has been deleted successfully.`;
                props.setDataToast({ open: true, type: "info", message: msg, timeAutoHide: 6000 });
                props.setRefreshData(prev => !prev);
            } else {
                window.alert("Something went wrong");
            }
        }).catch(error => {
            const resp = JSON.parse(error.request.response);
            const msg = resp.error ? resp.error : resp.message;
            props.setDataToast({ open: true, variant: "error", message: msg, timeAutoHide: 6000 });
        });
    };

    const tableRows = props.list.length > 0 && props.list.map((partner) => {
        return (
            <Table.Row key={partner.id}>
                <Table.Cell>{partner.id}</Table.Cell>
                <Table.Cell>{partner.created_at}</Table.Cell>
                <Table.Cell>{partner.name}</Table.Cell>
                <Table.Cell>{partner.app_key}</Table.Cell>
                <Table.Cell>{partner.organization}</Table.Cell>
                <Table.Cell>{partner.is_active ? "Enable" : "Disabled"}</Table.Cell>
                <Table.Cell textAlign='center'>
                    <Icon name='edit' color='blue' onClick={() => infoClick(partner)} data-key={partner.id} className='icon-button' style={{fontSize: "18px"}} />
                    <Icon name='delete' color='red' onClick={() => deleteConfirm(partner)} data-key={partner.id} className='icon-button' style={{fontSize: "18px"}} />
                </Table.Cell>
            </Table.Row>
        );
    });

    return (
        <Grid padded='vertically'>
            <Grid.Column>
                <Table sortable celled singleLine compact>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell style={{ width: 200 }}>ID</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: 200 }}>Created</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: 200 }}>Name</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: 200 }}>App Key</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: 200 }}>Organization</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: 200 }}>Status</Table.HeaderCell>
                            <Table.HeaderCell width={1}>{t('member_list_details')}</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {(props.status === STATUS_LOADING) ? loadingContent : tableRows}
                    </Table.Body>
                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell colSpan='6' textAlign='center' style={{ position: 'relative' }}>
                                <Pagination
                                    activePage={currentPage}
                                    totalPages={pageNumbers.length}
                                    onPageChange={handlePaginationChange}
                                    siblingRange={1}
                                    boundaryRange={1}
                                    disabled={pageNumbers.length === 1}
                                />
                                <PageSelect activePage={currentPage}
                                            pagesTotal={pageNumbers.length}
                                            onPageChange={handlePaginationChange}
                                />
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                </Table>
                <Confirm
                    header={"Please Confirm"}
                    open={showModalConfirm}
                    content={messageModalConfirm}
                    cancelButton={"Cancel"}
                    confirmButton={"Ok"}
                    onOpen={() => setShowModalConfirm(true)}
                    onCancel={() => setShowModalConfirm(false)}
                    onConfirm={deletePartner}
                    size='tiny'
                />
                <Modal open={showModal} onClose={onCloseModal} style={{width: "60%"}}>
                    <AddPartner
                        data={selectedPartner}
                        organizations={organizations}
                        setShowModal={setShowModal}
                        setDataToast={props.setDataToast}
                        onCloseModal={onCloseModal}
                        setRefreshData={props.setRefreshData}
                    />
                </Modal>
            </Grid.Column>
        </Grid>
    );
};

export default List;